import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { LOSS_TYPE_CHECKS } from './entryHelpers';

/** All non-pictures, non-document-checklist form fields for the edit drawer. */
export default function EntryFormFields({ formData, handleChange, companies }) {
  return (
    <>
      {/* Company Name */}
      <div>
        <label className="text-gray-500 text-sm block">Company Name</label>
        <Select value={formData.company_name} onValueChange={(v) => handleChange('company_name', v)}>
          <SelectTrigger className="w-full bg-white h-10 text-base border-gray-300" data-testid="edit-entry-company">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {companies.map((c) => (
              <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Date Of Loss */}
      <div>
        <label className="text-gray-500 text-sm block">Date Of Loss</label>
        <Input
          type="date"
          value={formData.date_of_loss}
          onChange={(e) => handleChange('date_of_loss', e.target.value)}
          className="w-full bg-white h-10 text-base border-gray-300"
          data-testid="edit-entry-date"
        />
      </div>

      {/* Drivers Name */}
      <div>
        <label className="text-gray-500 text-sm block">Drivers Name</label>
        <Input
          type="text"
          value={formData.drivers_name}
          onChange={(e) => handleChange('drivers_name', e.target.value)}
          className="w-full bg-white h-10 text-base border-gray-300"
          data-testid="edit-entry-driver-name"
        />
      </div>

      {/* Drivers Ph No */}
      <div>
        <label className="text-gray-500 text-sm block">Drivers Ph No</label>
        <Input
          type="tel"
          placeholder="10 Digit Phone #"
          value={formData.drivers_ph_no}
          onChange={(e) => handleChange('drivers_ph_no', e.target.value)}
          className="w-full bg-white h-10 text-base border-gray-300 placeholder:italic"
          maxLength={14}
          data-testid="edit-entry-driver-phone"
        />
      </div>

      {/* Truck No & Trailer No */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-gray-500 text-sm block">Truck No</label>
          <Input
            type="text"
            value={formData.truck_no}
            onChange={(e) => handleChange('truck_no', e.target.value)}
            className="w-full bg-white h-10 text-base border-gray-300"
            data-testid="edit-entry-truck"
          />
        </div>
        <div>
          <label className="text-gray-500 text-sm block">Trailer No</label>
          <Input
            type="text"
            value={formData.trailer_no}
            onChange={(e) => handleChange('trailer_no', e.target.value)}
            className="w-full bg-white h-10 text-base border-gray-300"
            data-testid="edit-entry-trailer"
          />
        </div>
      </div>

      {/* Location */}
      <div>
        <label className="text-gray-500 text-sm block">Location</label>
        <Input
          type="text"
          value={formData.location}
          onChange={(e) => handleChange('location', e.target.value)}
          className="w-full bg-white h-10 text-base border-gray-300"
          data-testid="edit-entry-location"
        />
      </div>

      {/* Description */}
      <div>
        <label className="text-gray-500 text-sm block">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={5}
          className="w-full bg-white text-base border-gray-300 resize-y"
          data-testid="edit-entry-description"
        />
      </div>

      {/* Loss Type Checkboxes */}
      <div className="flex items-center gap-6 pt-3 pb-1">
        {LOSS_TYPE_CHECKS.map(({ key, label }) => (
          <div key={key} className="flex items-center space-x-2">
            <Checkbox
              id={`entry-${key}`}
              checked={formData[key]}
              onCheckedChange={(checked) => handleChange(key, checked)}
              className="w-5 h-5 border-2"
              data-testid={`edit-entry-loss-${key}`}
            />
            <Label htmlFor={`entry-${key}`} className="text-base text-gray-800 cursor-pointer font-medium">
              {label}
            </Label>
          </div>
        ))}
      </div>

      {/* Other Vehicle Involved */}
      <div className="border-t pt-2">
        <label className="text-gray-500 text-sm block mb-1">Other Vehicle Involved</label>
        <RadioGroup value={formData.other_vehicle_involved} onValueChange={(v) => handleChange('other_vehicle_involved', v)}>
          <div className="flex gap-6">
            {['CMV', 'NA', 'Non-CMV'].map((val) => (
              <div key={val} className="flex items-center space-x-2">
                <RadioGroupItem
                  value={val}
                  id={`entry-ov-${val}`}
                  className="w-5 h-5"
                  data-testid={`edit-entry-ov-${val.toLowerCase()}`}
                />
                <Label htmlFor={`entry-ov-${val}`} className="text-base text-gray-800 cursor-pointer">{val}</Label>
              </div>
            ))}
          </div>
        </RadioGroup>
      </div>

      {/* Fault + Driver Charged */}
      <FaultRow formData={formData} handleChange={handleChange} />

      {/* Police Involved | Citation Issued | Police Report */}
      <PoliceRow formData={formData} handleChange={handleChange} />

      {/* Injury | Tow Away | Fatality */}
      <div className="grid grid-cols-3 sm:grid-cols-3 gap-4 border-t pt-2">
        {[
          { field: 'injury', label: 'Injury' },
          { field: 'tow_away', label: 'Tow Away' },
          { field: 'fatality', label: 'Fatality' },
        ].map(({ field, label }) => (
          <div key={field}>
            <label className="text-gray-500 text-sm block mb-1">{label}</label>
            <Checkbox
              id={`entry-${field}`}
              checked={formData[field]}
              onCheckedChange={(checked) => handleChange(field, checked)}
              className="w-5 h-5 border-2"
              data-testid={`edit-entry-${field}`}
            />
          </div>
        ))}
      </div>
    </>
  );
}

function FaultRow({ formData, handleChange }) {
  const onFaultChange = (v) => {
    handleChange('fault', v);
    if (v !== 'At Fault') {
      handleChange('driver_charged', '');
      handleChange('deductible', false);
      handleChange('deductible_amount', '');
      handleChange('other_amount_checked', false);
      handleChange('other_amount', '');
    }
  };
  const onDriverChargedChange = (v) => {
    handleChange('driver_charged', v);
    if (v !== 'Yes') {
      handleChange('deductible', false);
      handleChange('deductible_amount', '');
      handleChange('other_amount_checked', false);
      handleChange('other_amount', '');
    }
  };

  return (
    <div className="border-t pt-2">
      <div className="flex gap-10 items-start">
        <div>
          <label className="text-gray-500 text-sm block mb-1">Fault</label>
          <RadioGroup value={formData.fault} onValueChange={onFaultChange}>
            <div className="flex gap-6 mt-1">
              {['At Fault', 'Not At Fault', 'Unknown'].map((val) => (
                <div key={val} className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={val}
                    id={`entry-fault-${val}`}
                    className="w-5 h-5"
                    data-testid={`edit-entry-fault-${val.toLowerCase().replace(/ /g, '-')}`}
                  />
                  <Label htmlFor={`entry-fault-${val}`} className="text-base text-gray-800 cursor-pointer">{val}</Label>
                </div>
              ))}
            </div>
          </RadioGroup>
        </div>
        {formData.fault === 'At Fault' && (
          <div>
            <label className="text-gray-500 text-sm block mb-1">Driver Charged</label>
            <RadioGroup
              value={formData.driver_charged}
              onValueChange={onDriverChargedChange}
              className="flex items-center gap-3 mt-1.5"
              data-testid="driver-charged-group"
            >
              <div className="flex items-center space-x-1">
                <RadioGroupItem value="Yes" id="entry-dc-yes" data-testid="driver-charged-yes" />
                <Label htmlFor="entry-dc-yes" className="text-base text-gray-800 cursor-pointer">Yes</Label>
              </div>
              <div className="flex items-center space-x-1">
                <RadioGroupItem value="No" id="entry-dc-no" data-testid="driver-charged-no" />
                <Label htmlFor="entry-dc-no" className="text-base text-gray-800 cursor-pointer">No</Label>
              </div>
            </RadioGroup>
            {formData.driver_charged === 'Yes' && (
              <DeductibleOrOtherAmount formData={formData} handleChange={handleChange} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DeductibleOrOtherAmount({ formData, handleChange }) {
  return (
    <div className="flex flex-col gap-1.5 mt-2">
      {!formData.other_amount_checked && (
        <div>
          <div className="flex items-center gap-1.5">
            <Checkbox
              id="entry-deductible-cb"
              checked={formData.deductible}
              onCheckedChange={(v) => {
                handleChange('deductible', !!v);
                if (!v) handleChange('deductible_amount', '');
              }}
              data-testid="deductible-checkbox"
              className="w-3.5 h-3.5"
            />
            <Label htmlFor="entry-deductible-cb" className="text-xs text-gray-700 cursor-pointer">Deductible</Label>
          </div>
          {formData.deductible && (
            <Input
              type="text"
              placeholder="$0.00"
              value={formData.deductible_amount}
              onChange={(e) => handleChange('deductible_amount', e.target.value.replace(/[^0-9.]/g, ''))}
              className="w-28 h-7 text-xs mt-1 ml-5"
              data-testid="deductible-amount"
            />
          )}
        </div>
      )}
      {!formData.deductible && (
        <div>
          <div className="flex items-center gap-1.5">
            <Checkbox
              id="entry-other-amt-cb"
              checked={formData.other_amount_checked}
              onCheckedChange={(v) => {
                handleChange('other_amount_checked', !!v);
                if (!v) handleChange('other_amount', '');
              }}
              data-testid="other-amount-checkbox"
              className="w-3.5 h-3.5"
            />
            <Label htmlFor="entry-other-amt-cb" className="text-xs text-gray-700 cursor-pointer">Other amount</Label>
          </div>
          {formData.other_amount_checked && (
            <Input
              type="text"
              placeholder="$0.00"
              value={formData.other_amount}
              onChange={(e) => handleChange('other_amount', e.target.value.replace(/[^0-9.]/g, ''))}
              className="w-28 h-7 text-xs mt-1 ml-5"
              data-testid="other-amount-value"
            />
          )}
        </div>
      )}
    </div>
  );
}

function PoliceRow({ formData, handleChange }) {
  return (
    <div className="border-t pt-2">
      <div className={formData.police_involved === 'Yes' ? 'flex gap-8 items-start' : ''}>
        <div className={formData.police_involved === 'Yes' ? 'shrink-0' : ''}>
          <label className="text-gray-500 text-sm block mb-1">Police Involved</label>
          <RadioGroup value={formData.police_involved} onValueChange={(v) => handleChange('police_involved', v)}>
            <div className="flex gap-4">
              {['No', 'Yes'].map((val) => (
                <div key={val} className="flex items-center space-x-1">
                  <RadioGroupItem
                    value={val}
                    id={`entry-police-${val.toLowerCase()}`}
                    className="w-5 h-5"
                    data-testid={`edit-entry-police-${val.toLowerCase()}`}
                  />
                  <Label htmlFor={`entry-police-${val.toLowerCase()}`} className="text-base text-gray-800 cursor-pointer">{val}</Label>
                </div>
              ))}
            </div>
          </RadioGroup>
        </div>
        {formData.police_involved === 'Yes' && (
          <>
            <div className="shrink-0">
              <label className="text-gray-500 text-sm block mb-1">
                Citation Issued <span className="text-red-500">*</span>
              </label>
              <RadioGroup value={formData.citation} onValueChange={(v) => handleChange('citation', v)}>
                <div className="flex gap-4">
                  {['No', 'Yes'].map((val) => (
                    <div key={val} className="flex items-center space-x-1">
                      <RadioGroupItem
                        value={val}
                        id={`entry-citation-${val.toLowerCase()}`}
                        className="w-5 h-5"
                        data-testid={`edit-entry-citation-${val.toLowerCase()}`}
                      />
                      <Label htmlFor={`entry-citation-${val.toLowerCase()}`} className="text-base text-gray-800 cursor-pointer">{val}</Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </div>
            <div className="flex-1 min-w-0">
              <label className="text-gray-500 text-sm block mb-1">
                Police Report # {!formData.police_report_not_ready && <span className="text-red-500">*</span>}
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder="Report #"
                  value={formData.police_report_no}
                  onChange={(e) => handleChange('police_report_no', e.target.value)}
                  disabled={formData.police_report_not_ready}
                  className={`w-full bg-white h-9 text-sm border-gray-300 placeholder:italic placeholder:text-gray-400 ${
                    formData.police_report_not_ready ? 'opacity-50' : ''
                  } ${!formData.police_report_not_ready && !formData.police_report_no?.trim() ? 'border-red-400' : ''}`}
                  data-testid="edit-entry-police-report"
                />
                {!formData.police_report_no?.trim() && (
                  <div className="flex items-center space-x-1 shrink-0">
                    <Checkbox
                      id="entry-police-not-ready"
                      checked={formData.police_report_not_ready}
                      onCheckedChange={(checked) => handleChange('police_report_not_ready', checked)}
                      className="w-4 h-4 border-2"
                      data-testid="edit-entry-police-report-not-ready"
                    />
                    <Label htmlFor="entry-police-not-ready" className="text-xs text-gray-500 cursor-pointer whitespace-nowrap">
                      Not ready
                    </Label>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
